import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-black" data-testid="privacy-policy-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            <Link to="/" data-testid="logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_property-hub-465/artifacts/aqf0sdon_AIP1%20No%20Background.png" 
                alt="AIP Logo" 
                className="h-20 w-auto"
              />
            </Link>
            <Link to="/" className="text-neutral-400 hover:text-white flex items-center gap-2">
              <ArrowLeft size={18} /> Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-32 pb-20 px-6 md:px-12 lg:px-24">
        <motion.div 
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-neutral-500 mb-8">Last updated: December 2026</p>

          <div className="prose prose-invert max-w-none space-y-8 text-neutral-300">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Introduction</h2>
              <p>
                AIP Limited ("we", "us", "our") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our property inventory services.
              </p>
              <p>
                We are registered in England and Wales. For the purposes of the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018, we are the data controller.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Information We Collect</h2>
              <h3 className="text-xl font-medium text-white mb-2">Personal Information</h3>
              <p>We may collect the following personal information:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Name and contact details (email address, phone number)</li>
                <li>Property address and postcode</li>
                <li>Booking and appointment information</li>
                <li>Account login credentials (email and encrypted password)</li>
                <li>Communication records and correspondence</li>
                <li>Payment information (processed securely via third-party providers)</li>
              </ul>

              <h3 className="text-xl font-medium text-white mb-2 mt-4">Automatically Collected Information</h3>
              <p>When you visit our website, we automatically collect:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>IP address and browser type</li>
                <li>Device information and operating system</li>
                <li>Pages visited and time spent on site</li>
                <li>Referring website addresses</li>
                <li>Cookie data (see our Cookie Policy)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
              <p>We use your personal information for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Service Delivery:</strong> To provide property inventory reports, inspections, and related services</li>
                <li><strong>Booking Management:</strong> To schedule and manage your appointments</li>
                <li><strong>Communication:</strong> To respond to enquiries and send service-related notifications</li>
                <li><strong>Account Management:</strong> To create and manage your client portal account</li>
                <li><strong>Invoicing:</strong> To process payments and send invoices</li>
                <li><strong>Legal Compliance:</strong> To comply with legal obligations and protect our rights</li>
                <li><strong>Improvement:</strong> To improve our website and services</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Legal Basis for Processing</h2>
              <p>Under UK GDPR, we process your personal data on the following legal bases:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Contract:</strong> Processing necessary to perform our contract with you</li>
                <li><strong>Legitimate Interests:</strong> Processing necessary for our legitimate business interests</li>
                <li><strong>Legal Obligation:</strong> Processing necessary to comply with legal requirements</li>
                <li><strong>Consent:</strong> Where you have given consent for specific processing activities</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Data Sharing</h2>
              <p>We may share your personal information with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Service Providers:</strong> Third parties who assist in operating our website and services (hosting, email, analytics)</li>
                <li><strong>Professional Advisers:</strong> Lawyers, accountants, and insurers where necessary</li>
                <li><strong>Regulatory Bodies:</strong> Where required by law or to protect our legal rights</li>
              </ul>
              <p className="mt-4">We do not sell your personal information to third parties.</p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Data Retention</h2>
              <p>
                We retain your personal information for as long as necessary to provide our services and fulfil the purposes described in this policy. Property inventory reports and related documentation are retained for a minimum of 6 years to comply with legal requirements and support potential deposit disputes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Your Rights</h2>
              <p>Under UK data protection law, you have the following rights:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
                <li><strong>Erasure:</strong> Request deletion of your data (subject to legal obligations)</li>
                <li><strong>Restriction:</strong> Request restriction of processing</li>
                <li><strong>Portability:</strong> Request transfer of your data to another provider</li>
                <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
              </ul>
              <p className="mt-4">
                To exercise these rights, please contact us at <a href="mailto:inventories@aiplimited.co.uk" className="text-[#B8960B] hover:underline">inventories@aiplimited.co.uk</a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Data Security</h2>
              <p>
                We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction. These include encrypted data transmission (SSL/TLS), secure password hashing, and access controls.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. International Transfers</h2>
              <p>
                Your data may be transferred to and processed in countries outside the UK. Where this occurs, we ensure appropriate safeguards are in place to protect your data in compliance with UK data protection law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Complaints</h2>
              <p>
                If you have concerns about how we handle your personal data, please contact us first. You also have the right to lodge a complaint with the Information Commissioner's Office (ICO):
              </p>
              <p className="mt-2">
                <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#B8960B] hover:underline">www.ico.org.uk</a><br />
                Telephone: 0303 123 1113
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Contact Us</h2>
              <p>For questions about this Privacy Policy or our data practices, contact:</p>
              <p className="mt-2">
                AIP Limited<br />
                Email: <a href="mailto:inventories@aiplimited.co.uk" className="text-[#B8960B] hover:underline">inventories@aiplimited.co.uk</a><br />
                Phone: +44 7841 238 061<br />
                Location: Manchester, United Kingdom
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
